<template>
  <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
    <BreadCrumb PageTitle="Préparation & emballage" />
    <router-link v-if="auth.can('ADD_BL_S')" :to="{ name: 'AddNoteBLStockPage' }" class="btn pk-btn mb-3">
      <i class="ph ph-plus-circle me-1"></i>Créer un bon
    </router-link>
  </div>

  <div class="row g-3 mb-25">
    <div class="col-6 col-md-3" v-for="t in tabs" :key="t.key">
      <button
        type="button"
        class="pk-kpi w-100 text-start border rounded-3 p-20 bg-white d-flex align-items-center"
        :class="{ active: tab === t.key }"
        @click="setTab(t.key)"
      >
        <span class="pk-kpi-icon me-15"><i :class="t.icon"></i></span>
        <span>
          <span class="d-block fs-28 fw-bold lh-1">{{ counts[t.key] }}</span>
          <span class="text-muted">{{ t.label }}</span>
        </span>
      </button>
    </div>
  </div>

  <div class="row">
    <div :class="selected ? 'col-xl-8' : 'col-12'">
      <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
        <div class="card-body p-15 p-sm-20 p-md-25">
          <div class="d-flex flex-wrap align-items-center gap-2 mb-20">
            <input
              type="text"
              class="form-control shadow-none text-black rounded-0 pk-search"
              placeholder="Rechercher un bon, une agence, un opérateur..."
              v-model="filters.searchTerm"
              @input="onSearch"
            />
          </div>
          <div class="d-flex flex-wrap align-items-center gap-2 mb-20">
            <span class="text-muted fs-14 me-1"><i class="ph ph-calendar-blank me-1"></i>Date de création :</span>
            <button
              v-for="p in periods"
              :key="p.key"
              type="button"
              class="btn btn-sm"
              :class="period === p.key ? 'pk-btn' : 'btn-outline-secondary'"
              @click="setPeriod(p.key)"
            >
              {{ p.label }}
            </button>
            <template v-if="period === 'custom'">
              <input type="date" class="form-control form-control-sm pk-date" v-model="dateFrom" :max="dateTo || undefined" @change="getRecords(1)" aria-label="Du" />
              <span class="text-muted">→</span>
              <input type="date" class="form-control form-control-sm pk-date" v-model="dateTo" :min="dateFrom || undefined" @change="getRecords(1)" aria-label="Au" />
            </template>
            <button v-if="hasFilters" type="button" class="btn btn-sm btn-link text-danger" @click="resetFilters">
              <i class="ph ph-x-circle me-1"></i>Réinitialiser
            </button>
          </div>

          <vue-good-table :columns="columns" :rows="rows" :loading="isLoader">
            <template #emptystate>
              <center>Aucun bon dans cette liste</center>
            </template>
            <template #table-row="props">
              <template v-if="props.column.field === 'note_stock_ref'">
                <a href="javascript:void(0);" class="fw-bold" @click="openPanel(props.row)">
                  {{ props.row.note_stock_ref }}
                </a>
                <div class="text-muted fs-12">{{ getDate(props.row.created_at) }}</div>
              </template>
              <template v-else-if="props.column.field === 'note_stock_total_parcel'">
                {{ props.row.note_stock_total_parcel }} colis
              </template>
              <template v-else-if="props.column.field === 'problems'">
                <a v-if="Number(props.row.problems) > 0" href="javascript:void(0);" class="badge bg-danger text-decoration-none" @click="openPanel(props.row)">
                  <i class="ph ph-warning me-1"></i>{{ props.row.problems }}
                </a>
                <span v-else class="text-muted">—</span>
              </template>
              <template v-else-if="props.column.field === 'progress'">
                <div class="fs-13 mb-1">{{ props.row.packed }} / {{ props.row.note_stock_total_parcel }}</div>
                <div class="progress" style="height: 6px; min-width: 110px">
                  <div
                    class="progress-bar pk-bar"
                    :style="{ width: percent(props.row) + '%' }"
                  ></div>
                </div>
              </template>
              <template v-else-if="props.column.field === 'packing_by_name'">
                {{ props.row.packing_by_name || "—" }}
              </template>
              <template v-else-if="props.column.field === 'action'">
                <div class="d-flex flex-column gap-1">
                  <router-link
                    v-if="!props.row.note_stock_delivered && Number(props.row.packed) < Number(props.row.note_stock_total_parcel)"
                    class="btn btn-sm pk-btn"
                    :to="stationRoute(props.row)"
                  >
                    <i class="ph ph-play me-1"></i>
                    {{ Number(props.row.packed) > 0 ? "Reprendre" : "Démarrer l'emballage" }}
                  </router-link>
                  <router-link
                    v-else
                    class="btn btn-sm btn-outline-secondary"
                    :to="{ name: 'EditNoteBLStockPage', params: { id: props.row.id, refNote: props.row.note_stock_ref } }"
                  >
                    <i class="ph ph-eye me-1"></i>Voir le détail
                  </router-link>
                  <button type="button" class="btn btn-sm btn-outline-secondary" @click="openPanel(props.row)">
                    <i class="ph ph-file-text me-1"></i>Bon de préparation
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary"
                    :disabled="!Number(props.row.note_stock_total_parcel) || busy === 'labels-' + props.row.id"
                    @click="printLabels(props.row.id)"
                  >
                    <span v-if="busy === 'labels-' + props.row.id" class="spinner-border spinner-border-sm me-1"></span>
                    <i v-else class="ph ph-tag me-1"></i>Imprimer les étiquettes
                  </button>
                </div>
              </template>
              <span v-else>{{ props.formattedRow[props.column.field] }}</span>
            </template>
          </vue-good-table>
          <PaginatorContent :pagination="filters" :offset="9" @paginate="getRecords" />
        </div>
      </div>
    </div>

    <!-- Bon de préparation -->
    <div class="col-xl-4" v-if="selected">
      <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
        <div class="card-body p-20 p-md-25">
          <div class="d-flex justify-content-between align-items-start">
            <div>
              <h4 class="mb-5">Bon de préparation</h4>
              <h5 class="mb-10">{{ panel.note?.ref }}</h5>
            </div>
            <button type="button" class="btn-close" @click="selected = null"></button>
          </div>
          <template v-if="panel.note">
            <dl class="row mb-20 fs-14">
              <dt class="col-5 fw-normal text-muted">Agence</dt>
              <dd class="col-7">{{ panel.note.agency }}</dd>
              <dt class="col-5 fw-normal text-muted">Date de création</dt>
              <dd class="col-7">{{ getDate(panel.note.created_at) }}</dd>
              <dt class="col-5 fw-normal text-muted">Nombre de colis</dt>
              <dd class="col-7">{{ panel.note.total }}</dd>
              <dt class="col-5 fw-normal text-muted">Emballés</dt>
              <dd class="col-7">{{ panel.note.packed }} / {{ panel.note.total }}</dd>
            </dl>
            <h6 class="mb-10">Produits à préparer ({{ panel.products.length }})</h6>
            <table class="table table-sm">
              <thead>
                <tr><th>Référence</th><th>Quantité</th><th>Emplacement</th></tr>
              </thead>
              <tbody>
                <tr v-for="p in panel.products" :key="p.reference + p.location">
                  <td>{{ p.reference }}</td>
                  <td>{{ p.qty }}</td>
                  <td>{{ p.location || "—" }}</td>
                </tr>
              </tbody>
            </table>
            <div class="alert alert-light border fs-13">
              Total : {{ panel.products.length }} références • {{ panel.total_units }} unités<br />
              À répartir dans {{ panel.note.total }} colis
            </div>
            <template v-if="panel.problems && panel.problems.length">
              <h6 class="mb-10 text-danger"><i class="ph ph-warning me-1"></i>Problèmes signalés ({{ panel.problems.filter((x) => !x.resolved_at).length }} en cours)</h6>
              <ul class="list-unstyled fs-13 mb-20">
                <li v-for="pr in panel.problems" :key="pr.id" class="border rounded p-2 mb-2" :class="{ 'opacity-50': pr.resolved_at }">
                  <b>{{ pr.parcel_code }}</b> — {{ pr.reason }}
                  <div class="text-muted">{{ pr.reported_by_name }} · {{ getDate(pr.created_at) }}</div>
                  <button v-if="!pr.resolved_at" type="button" class="btn btn-sm btn-outline-success mt-1" @click="resolveProblem(pr)">
                    <i class="ph ph-check me-1"></i>Marquer comme résolu
                  </button>
                  <span v-else class="badge bg-success mt-1">Résolu par {{ pr.resolved_by_name }}</span>
                </li>
              </ul>
            </template>
            <div class="d-flex flex-column gap-2">
              <div class="d-flex gap-2">
                <button type="button" class="btn pk-btn flex-fill" :disabled="!!busy" @click="printSheet">
                  <span v-if="busy === 'print'" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else class="ph ph-printer me-1"></i>Imprimer
                </button>
                <button type="button" class="btn btn-outline-secondary flex-fill" :disabled="!!busy" @click="downloadSheet">
                  <span v-if="busy === 'download'" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else class="ph ph-download-simple me-1"></i>Télécharger
                </button>
              </div>
              <button type="button" class="btn btn-outline-secondary w-100" :disabled="!panel.note.total || !!busy" @click="printLabels(panel.note.id)">
                <i class="ph ph-tag me-1"></i>Imprimer les étiquettes
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, reactive, ref } from "vue";
import moment from "moment";
import Swal from "sweetalert2";
import api from "@/utils/axios";
import { useAuthStore } from "@/stores/auth";
import BreadCrumb from "@/components/Common/BreadCrumb.vue";

export default defineComponent({
  name: "PackingListPage",
  components: { BreadCrumb },
  setup() {
    const auth = useAuthStore();
    const tab = ref("todo");
    const tabs = [
      { key: "all", label: "Tous", icon: "ph ph-list-bullets" },
      { key: "todo", label: "À préparer", icon: "ph ph-package" },
      { key: "doing", label: "En cours", icon: "ph ph-arrows-clockwise" },
      { key: "done", label: "Terminés", icon: "ph ph-check-circle" },
    ];
    const counts = reactive({ all: 0, todo: 0, doing: 0, done: 0 });
    const rows = ref<any[]>([]);
    const isLoader = ref(false);
    const filters = reactive<any>({ searchTerm: "", total: 0, per_page: 10, from: 1, to: 0, current_page: 1 });
    const period = ref("");
    const dateFrom = ref("");
    const dateTo = ref("");
    const periods = [
      { key: "today", label: "Aujourd'hui" },
      { key: "yesterday", label: "Hier" },
      { key: "7days", label: "7 derniers jours" },
      { key: "month", label: "Ce mois" },
      { key: "custom", label: "Personnalisé" },
    ];
    const hasFilters = computed(() => !!(period.value || filters.searchTerm || tab.value !== "todo"));
    const selected = ref<any>(null);
    const panel = reactive<any>({ note: null, products: [], total_units: 0 });

    const columns = [
      { label: "Bon STOCK", field: "note_stock_ref", sortable: false },
      { label: "Colis", field: "note_stock_total_parcel", sortable: false },
      { label: "Progression", field: "progress", sortable: false },
      { label: "Opérateur", field: "packing_by_name", sortable: false },
      { label: "Problèmes", field: "problems", sortable: false },
      { label: "Actions", field: "action", sortable: false },
    ];

    async function getRecords(page = 1) {
      filters.current_page = page;
      isLoader.value = true;
      try {
        const res = await api.get("/api/auth/packing/notes", {
          params: {
            tab: tab.value,
            searchTerm: filters.searchTerm,
            page,
            per_page: filters.per_page,
            period: period.value || undefined,
            date_from: period.value === "custom" ? dateFrom.value || undefined : undefined,
            date_to: period.value === "custom" ? dateTo.value || undefined : undefined,
          },
        });
        Object.assign(counts, res.data.counts);
        rows.value = res.data.users.data;
        Object.assign(filters, res.data.users);
      } catch (e) {
        console.error(e);
      } finally {
        isLoader.value = false;
      }
    }
    function setTab(k: string) {
      tab.value = k;
      selected.value = null;
      getRecords(1);
    }
    function setPeriod(k: string) {
      period.value = period.value === k ? "" : k; // click again to clear
      if (period.value !== "custom") {
        dateFrom.value = "";
        dateTo.value = "";
      }
      selected.value = null;
      // custom: wait for the dates before querying
      if (period.value !== "custom") getRecords(1);
    }
    function resetFilters() {
      period.value = "";
      dateFrom.value = "";
      dateTo.value = "";
      filters.searchTerm = "";
      tab.value = "todo";
      selected.value = null;
      getRecords(1);
    }
    let timer: any = null;
    function onSearch() {
      clearTimeout(timer);
      timer = setTimeout(() => getRecords(1), 350);
    }
    const percent = (r: any) =>
      Math.round((Number(r.packed) / Math.max(1, Number(r.note_stock_total_parcel))) * 100);
    const getDate = (v: any) => (v ? moment(String(v)).format("DD/MM/YYYY HH:mm") : "");
    const stationRoute = (r: any) => ({
      name: "PackingStationPage",
      params: { id: r.id, refNote: r.note_stock_ref },
    });

    async function openPanel(r: any) {
      selected.value = r;
      panel.note = null;
      try {
        const res = await api.get(`/api/auth/packing/notes/${r.id}`);
        Object.assign(panel, res.data);
      } catch (e) {
        console.error(e);
      }
    }
    async function resolveProblem(pr: any) {
      await api.post(`/api/auth/packing/problem/${pr.id}/resolve`);
      await openPanel(selected.value);
      getRecords(filters.current_page);
    }
    const busy = ref("");

    async function fetchPdf(path: string, params: Record<string, any> = {}) {
      const res = await api.get(path, { params, responseType: "arraybuffer" });
      return window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    }
    function failed() {
      Swal.fire({ icon: "error", title: "Oops...", text: "Impossible de générer le document" });
    }
    // print: the PDF is loaded in a hidden frame and the browser's print dialog opens on it
    function printUrl(url: string) {
      const frame = document.createElement("iframe");
      frame.style.cssText = "position:fixed;width:0;height:0;border:0;visibility:hidden";
      frame.src = url;
      frame.onload = () => {
        try {
          frame.contentWindow?.focus();
          frame.contentWindow?.print();
        } catch {
          window.open(url, "_blank"); // browser blocks printing from a frame: open the PDF instead
        }
        setTimeout(() => frame.remove(), 60000);
      };
      document.body.appendChild(frame);
    }
    async function printSheet() {
      busy.value = "print";
      try {
        printUrl(await fetchPdf(`/api/auth/packing/notes/${panel.note.id}/sheet`));
      } catch {
        failed();
      } finally {
        busy.value = "";
      }
    }
    async function downloadSheet() {
      busy.value = "download";
      try {
        const url = await fetchPdf(`/api/auth/packing/notes/${panel.note.id}/sheet`, { download: 1 });
        const a = document.createElement("a");
        a.href = url;
        a.download = `BON-PREPARATION-${panel.note.ref}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch {
        failed();
      } finally {
        busy.value = "";
      }
    }
    async function printLabels(noteId: number) {
      busy.value = "labels-" + noteId;
      try {
        window.open(await fetchPdf(`/api/auth/packing/notes/${noteId}/labels`), "_blank");
      } catch {
        failed();
      } finally {
        busy.value = "";
      }
    }

    onMounted(() => getRecords());

    return { busy, downloadSheet, printLabels, auth, period, periods, dateFrom, dateTo, hasFilters, setPeriod, resetFilters, tab, tabs, counts, rows, isLoader, filters, columns, selected, panel, getRecords, setTab, onSearch, percent, getDate, stationRoute, openPanel, printSheet, resolveProblem };
  },
});
</script>

<style scoped>
.pk-kpi { cursor: pointer; transition: box-shadow 0.15s, border-color 0.15s; }
.pk-kpi.active { border-color: var(--ui-accent) !important; box-shadow: 0 0 0 2px rgba(var(--ui-accent-rgb), 0.2); }
.pk-kpi-icon { font-size: 26px; color: var(--ui-accent); }
.pk-btn { background: var(--ui-accent); color: #fff !important; border: 0; font-weight: 600; white-space: nowrap; }
.pk-btn:hover { background: var(--ui-accent-strong); color: #fff; }
.pk-search { flex: 1; min-width: 220px; }
.pk-date { width: 150px; }
.pk-bar { background: var(--ui-accent); }
</style>
